import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteConfig } from '@/mocks/siteConfig';
import { getFormUrl } from '@/../tools/form';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    foundingDate: siteConfig.founded,
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteConfig.email,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteConfig.name}`,
    url: `${siteConfig.url}/about`,
    description: `了解${siteConfig.name}的品牌故事、用户口碑以及联系方式。${siteConfig.description}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
    about: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
  };

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Page Header */}
        <section className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 text-center">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
              关于韩漫画网
            </h1>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              致力于为中国读者提供最好的韩国漫画在线阅读体验
            </p>
          </div>
        </section>

        {/* Tab Navigation */}
        <section className="border-b border-background-200/70 bg-background-50 sticky top-16 z-40">
          <div className="max-w-3xl mx-auto px-4">
            <div className="flex items-center justify-center gap-2 py-2">
              {[
                { key: 'intro', label: '品牌介绍', icon: 'ri-building-2-line' },
                { key: 'reputation', label: '用户口碑', icon: 'ri-thumb-up-line' },
                { key: 'contact', label: '联系我们', icon: 'ri-mail-line' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as typeof activeTab)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                    activeTab === tab.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  <i className={tab.icon}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          {activeTab === 'intro' && (
            <article className="space-y-8">
              <div>
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">
                  我们的故事
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  韩漫画网成立于2020年，是中国最早的专注于韩国漫画在线阅读的平台之一。我们怀着对韩国漫画的热爱，致力于为广大中文读者打造一个优质、便捷、完全免费的韩漫在线阅读平台。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  经过多年的发展，韩漫画网已经积累了丰富的韩国漫画资源，涵盖<strong>恋爱、动作、奇幻、悬疑、搞笑、剧情</strong>等多种类型。我们独特的<strong>下拉式阅读</strong>模式，让读者无需翻页即可流畅阅读漫画，获得了数百万用户的一致好评。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们的团队由一群热爱漫画的年轻人组成，包括专业的翻译团队和技术开发团队。我们坚持<strong>每日更新</strong>，确保读者能在第一时间阅读到最新的韩国漫画章节。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {[
                  { icon: 'ri-user-heart-line', title: '用户至上', desc: '一切以读者体验为核心，持续优化阅读体验和内容质量' },
                  { icon: 'ri-book-open-line', title: '内容丰富', desc: '精心挑选优质韩国漫画，涵盖多种类型满足不同读者需求' },
                  { icon: 'ri-shield-star-line', title: '品质保证', desc: '专业团队把关内容质量，确保每部漫画都值得阅读' },
                ].map((value) => (
                  <div key={value.title} className="bg-background-100 rounded-lg p-5 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                      <i className={`${value.icon} text-xl text-primary-500`}></i>
                    </div>
                    <h3 className="font-semibold text-sm text-foreground-950 mb-2">{value.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{value.desc}</p>
                  </div>
                ))}
              </div>

              <div>
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">
                  我们的使命
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  韩漫画网的使命是让每一位中文读者都能轻松便捷地享受韩国漫画的魅力。我们相信好的内容应该被更多人看到，所以我们坚持<strong>完全免费</strong>的模式，让优质的韩国漫画真正走进每一位读者的生活。
                  未来，我们将继续拓展内容资源，优化阅读体验，努力成为中文世界最受欢迎的韩国漫画在线阅读平台。
                </p>
              </div>
            </article>
          )}

          {activeTab === 'reputation' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">
                  读者好评
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  韩漫画网自上线以来，已服务超过<strong>数百万</strong>漫画爱好者，收获了无数好评。以下是部分读者的真实反馈：
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { name: '小雨', rating: 5, text: '韩漫画网真的是我目前用过最好的韩漫阅读平台！下拉式阅读实在太方便了，一口气看了几十话，完全停不下来。而且完全免费，强烈推荐！', date: '2024-12-15' },
                  { name: '星辰大海', rating: 5, text: '作为一个韩漫重度爱好者，韩漫画网的更新速度真的让人满意。每天都有新章节，翻译质量也很高，已经推荐给身边所有喜欢韩漫的朋友了。', date: '2024-11-28' },
                  { name: '墨墨', rating: 5, text: '从2021年就开始用韩漫画网了，见证了平台的成长。现在的界面越来越好用，分类也很清晰，找漫画非常方便。希望韩漫画网越来越好！', date: '2024-11-10' },
                  { name: '风铃', rating: 4, text: '韩漫画网的资源真的很全，想看的韩漫基本都能找到。下拉式阅读体验很流畅，手机上看也很舒服。唯一的建议是希望漫画更新提醒功能能再快一点。', date: '2024-10-22' },
                  { name: '可乐不加冰', rating: 5, text: '完全免费的韩漫平台，内容还这么全，真的很难得。特别喜欢奇幻分类的漫画，每天必刷。感谢韩漫画网团队的辛勤付出！', date: '2024-10-05' },
                ].map((review) => (
                  <div key={review.name} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1.5">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 font-semibold text-xs">
                          {review.name[0]}
                        </div>
                        <span className="font-semibold text-sm text-foreground-950">{review.name}</span>
                      </div>
                      <span className="text-xs text-foreground-400">{review.date}</span>
                    </div>
                    <div className="flex items-center gap-0.5 mb-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                  </div>
                ))}
              </div>

              <div className="bg-primary-50 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold font-heading text-primary-500 mb-2">4.8</div>
                <div className="flex items-center justify-center gap-0.5 mb-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-lg"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600">综合用户评分 · 基于数千条真实评价</p>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们联系。我们会在1-2个工作日内回复您的消息。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
                {[
                  { icon: 'ri-mail-line', label: '电子邮箱', value: siteConfig.email },
                  { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00-18:00' },
                  { icon: 'ri-message-2-line', label: '响应时间', value: '1-2个工作日' },
                ].map((item) => (
                  <div key={item.label} className="bg-background-100 rounded-lg p-5 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                      <i className={`${item.icon} text-lg text-primary-500`}></i>
                    </div>
                    <h3 className="font-semibold text-sm text-foreground-950 mb-1">{item.label}</h3>
                    <p className="text-xs text-foreground-500">{item.value}</p>
                  </div>
                ))}
              </div>

              {/* Contact Form - to be implemented with form URL */}
              <div className="bg-background-100 rounded-xl p-6">
                <h3 className="font-semibold text-base text-foreground-950 mb-4">发送消息</h3>
                <form
                  id="contact-form-about"
                  data-readdy-form=""
                  data-submit-addr="https://readdy.ai/api/form/d9pf2l1n2trhb35gvea0"
                  className="space-y-4"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formElement = e.currentTarget;
                    const honeypot = (formElement.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                    if (honeypot) {
                      const successEl = document.getElementById('contact-success-msg');
                      if (successEl) successEl.style.display = 'block';
                      return;
                    }
                    try {
                      const formData = new FormData(formElement);
                      const submitAddr = formElement.getAttribute('data-submit-addr') || '';
                      if (!submitAddr) return;
                      const params = new URLSearchParams();
                      formData.forEach((value, key) => {
                        if (key !== 'phone_alt') params.append(key, value as string);
                      });
                      const response = await fetch(submitAddr, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: params.toString(),
                      });
                      const responseText = await response.text();
                      let parsed;
                      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
                      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
                      if (response.ok && parsed?.code === 'OK') {
                        const successEl = document.getElementById('contact-success-msg');
                        if (successEl) successEl.style.display = 'block';
                        const errorEl = document.getElementById('contact-error-msg');
                        if (errorEl) errorEl.style.display = 'none';
                        formElement.reset();
                      } else {
                        const errorEl = document.getElementById('contact-error-msg');
                        if (errorEl) {
                          errorEl.textContent = serverMsg || '提交失败，请稍后再试';
                          errorEl.style.display = 'block';
                        }
                      }
                    } catch {
                      const errorEl = document.getElementById('contact-error-msg');
                      if (errorEl) {
                        errorEl.textContent = '网络错误，请稍后再试';
                        errorEl.style.display = 'block';
                      }
                    }
                  }}
                >
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 text-foreground-950"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 text-foreground-950"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 text-foreground-950 resize-none"
                      placeholder="请输入您的消息内容（最多500字）"
                    ></textarea>
                  </div>
                  <div id="contact-success-msg" className="hidden bg-primary-100 text-primary-700 px-4 py-3 rounded-lg text-sm">
                    消息已发送成功！我们会尽快回复您。
                  </div>
                  <div id="contact-error-msg" className="hidden bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
                  </div>
                  <button
                    type="submit"
                    className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full font-semibold text-sm transition-colors whitespace-nowrap"
                  >
                    发送消息
                  </button>
                </form>
              </div>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}